import { motion } from "framer-motion";
import { Leaf, Target } from "lucide-react";
import { useLanguage } from "../i18n";

const IMG_SUSTAIN =
  "https://images.unsplash.com/photo-1623136299195-570a06bdae6b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBhZ3JpY3VsdHVyZSUyMGdyZWVuaG91c2UlMjBleHBvcnR8ZW58MHx8fHwxNzg5NTA0MjE5fDA&ixlib=rb-4.1.0&q=85";

const reveal = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
};

export const SustainabilityBanner = () => {
  const { t } = useLanguage();

  return (
    <section id="surdurulebilirlik" data-testid="sustainability-section" className="bg-white py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="overflow-hidden rounded-3xl bg-[#e9faf1]">
          <div className="grid lg:grid-cols-2">
            <div className="flex flex-col items-center justify-center p-8 text-center sm:p-12 lg:p-16">
              <motion.p {...reveal} className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-[#1d8a52]">
                {t.sustainability.overline}
              </motion.p>
              <motion.h2
                {...reveal}
                transition={{ ...reveal.transition, delay: 0.08 }}
                className="font-display mt-4 text-2xl font-bold leading-tight tracking-tight text-[#062316] sm:text-3xl lg:text-4xl"
                data-testid="sustainability-title"
              >
                {t.sustainability.title}
              </motion.h2>
              <motion.p
                {...reveal}
                transition={{ ...reveal.transition, delay: 0.15 }}
                className="mx-auto mt-6 max-w-lg text-base leading-relaxed text-[#1a3d2b]"
              >
                {t.sustainability.text}
              </motion.p>

              <motion.div
                {...reveal}
                transition={{ ...reveal.transition, delay: 0.22 }}
                className="mt-8 grid w-full grid-cols-1 gap-3 sm:grid-cols-2"
              >
                {t.sustainability.badges.map((b) => (
                  <span
                    key={b}
                    className="flex items-center gap-2.5 rounded-xl border border-[#48e38e]/50 bg-white/70 px-4 py-3 text-sm font-medium text-[#0a2b1b] transition-colors duration-300 hover:bg-white"
                  >
                    <Leaf size={15} className="shrink-0 text-[#1d8a52]" />
                    {b}
                  </span>
                ))}
              </motion.div>

              <motion.div
                {...reveal}
                transition={{ ...reveal.transition, delay: 0.3 }}
                className="mt-8 flex w-full items-start gap-4 rounded-2xl bg-[#062316] p-6 text-left"
                data-testid="sustainability-goal"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#48e38e]/15 text-[#48e38e]">
                  <Target size={20} />
                </span>
                <div>
                  <h3 className="font-display text-base font-semibold text-white">{t.sustainability.goal}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-white/60">{t.sustainability.goalText}</p>
                </div>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 1.06 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
              className="relative min-h-[320px] lg:min-h-full"
            >
              <img
                src={IMG_SUSTAIN}
                alt="Lush controlled ecological agriculture greenhouse"
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#e9faf1] via-transparent to-transparent lg:bg-gradient-to-r" />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
